import { useColorMode } from '@vueuse/core'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { apiDate } from '@/libs/format'
import i18n from '@/libs/i18n'
import { clearModuleRefusal } from '@/libs/license'
import { componentSoldOut, OFFER_WITHDRAWN_STATUS, offerWithdrawn } from '@/libs/offer'
import { useFetchStore } from '@/stores/fetch'
import type {
    IBookingAttribution, IBookingComponentLine, IBookingConfirmation, IBookingOffer, IBookingOffers,
    IBookingPaymentPage, IBookingPaymentStatus, IBookingProperty,
} from '@/types/Booking'

/**
 * The whole visit, in one store.
 *
 * A booking is five screens with one thread running through them — the dates chosen on
 * the first, the room chosen on the second, the name typed on the third — so the state
 * is held here rather than passed from route to route. A router query string carrying
 * a chosen room and a price would be a price the visitor could edit.
 *
 * The dates and the party are kept in sessionStorage, so a reload halfway through does
 * not send somebody back to an empty form. The chosen room deliberately is NOT: it may
 * have been sold in the meantime, and a room restored from storage is a room the site
 * would be promising without having asked.
 */

const ATTRIBUTION_KEY = 'booking.attribution'
const PAYMENT_KEY = 'booking.payment'
const SEARCH_KEY = 'booking.search'

/**
 * The marketing offer's signed context, kept for the whole visit.
 *
 * Beside the campaign and for the same reason: a guest lands on an offer's landing
 * page, presses Rezerviraj, and books four screens later — and the price they were
 * promised has to survive all four. It is an opaque token naming the offer and nothing
 * else; the discount, the price list and the price itself are the server's, worked out
 * again on every quote (docs/MARKETING.md §10).
 */
const OFFER_KEY = 'booking.offer'

const emptyAttribution = (): IBookingAttribution => ({
    campaign_id: '', utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '',
})

export const useBookingStore = defineStore('booking', () => {
    const fetchStore = useFetchStore()

    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    const property = ref<IBookingProperty>()
    const loading = ref(false)

    /** Set when the deployment sells nothing on the web, or this house does not. */
    const closed = ref(false)

    const arrival = ref('')
    const departure = ref('')
    const adults = ref(2)
    const children = ref(0)

    const offers = ref<IBookingOffers>()
    const chosen = ref<IBookingOffer>()

    const firstName = ref('')
    const lastName = ref('')
    const email = ref('')
    const phone = ref('')
    const notes = ref('')
    const termsAccepted = ref(false)

    const confirmation = ref<IBookingConfirmation>()
    const paying = ref(false)

    const attribution = ref<IBookingAttribution>(emptyAttribution())

    /**
     * The offer's signed context, and the fact that the house has withdrawn it.
     *
     * offerRefused is what turns the ordinary quote into an explanation: the campaign
     * price is gone — the offer ended, was taken down, or was rewritten since the link
     * was made — and the guest is shown the house's own prices with a sentence saying
     * so, rather than a dead end on a link somebody put on Facebook months ago.
     */
    const offerToken = ref('')
    const offerRefused = ref(false)

    /**
     * The part of the package that has run out for these dates, in the house's own
     * words (docs/MARKETING.md §12).
     *
     * A string and not a boolean, because the only useful thing this refusal carries is
     * WHICH part is gone — "Parkirno mesto" — and this site cannot know what a house
     * called its car park. Empty means the package is deliverable, which is the state
     * of every quote that was never refused for one.
     *
     * The token is deliberately NOT dropped when it is set: the house still sells this
     * package, and quoting the ordinary price would hand the guest a room while quietly
     * removing the thing they came for.
     */
    const componentGone = ref('')

    /** The offer's name and reduction, as the last quote reported them. */
    const offerName = computed((): string => offers.value?.offer_name ?? '')

    /* The package's parts, as the last quote priced them. Split by who pays, because
     * "what you get" and "what it costs extra" are two things a guest reads for and
     * mixing them is how somebody misses the word that mattered. */
    const components = computed((): IBookingComponentLine[] => offers.value?.components ?? [])

    const includedComponents = computed((): IBookingComponentLine[] =>
        components.value.filter(line => line.price_mode !== 'surcharge'))

    const surchargeComponents = computed((): IBookingComponentLine[] =>
        components.value.filter(line => line.price_mode === 'surcharge'))

    /** What the supplements come to, in cents. Added to the room's own total ON THE
     *  SCREEN and never inside it: the room amount is what the reservation is written
     *  with, and folding a supplement into it would bill it twice. */
    const componentsAmount = computed((): number => offers.value?.components_amount ?? 0)
    const offerDiscountPercent = computed((): number => Math.round((offers.value?.offer_discount_bp ?? 0) / 100))
    const hasOffer = computed((): boolean => !!offers.value?.offer_id)

    const nights = computed((): number => {
        if (!arrival.value || !departure.value) return 0

        return Math.max(0, dayjs(departure.value).diff(dayjs(arrival.value), 'day'))
    })

    const currency = computed((): string => property.value?.currency ?? 'EUR')

    /** The house's own language — what a text falls back to when the reader's is missing. */
    const houseLanguage = computed((): string => property.value?.default_language ?? 'sl')

    const maxNights = computed((): number => property.value?.max_nights ?? 21)

    /**
     * Where the visitor came from, taken once from the address they arrived on.
     *
     * Read at the first page draw and then kept for the whole visit, including across
     * a reload — a guest who lands on an advertisement and books four screens later
     * still books on that advertisement, and the campaign that paid for them has to be
     * able to say so. The first campaign wins: somebody who arrives on an
     * advertisement and then navigates back through a bare link has still been brought
     * by the advertisement.
     */
    const readAttribution = (query: Record<string, unknown>) => {
        const stored = sessionStorage.getItem(ATTRIBUTION_KEY)

        if (stored) {
            try {
                attribution.value = { ...emptyAttribution(), ...JSON.parse(stored) }

                return
            } catch {
                // A storage value that will not parse is one somebody edited. Fall
                // through and read the address instead of trusting it.
            }
        }

        const one = (key: string): string => {
            const value = query[key]

            return typeof value === 'string' ? value.slice(0, 120) : ''
        }

        const found: IBookingAttribution = {
            campaign_id:  one('cmp') || one('campaign_id'),
            utm_source:   one('utm_source'),
            utm_medium:   one('utm_medium'),
            utm_campaign: one('utm_campaign'),
            utm_term:     one('utm_term'),
            utm_content:  one('utm_content'),
        }

        const carriesSomething = Object.values(found).some(value => value !== '')

        if (!carriesSomething) return

        attribution.value = found
        sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(found))
    }

    /**
     * The offer half of the same address: ?offer= and the dates the landing page put
     * on the link.
     *
     * Read in the same guard as the campaign and kept the same way, because it is the
     * same journey — what changes is that this one decides the PRICE, so losing it
     * between two screens is the guest being quoted the undiscounted figure they came
     * here to avoid.
     *
     * The dates are filled in only when the form has nothing yet: an offer's link
     * carries the package's own nights, and a guest who has since chosen their own
     * must keep them.
     */
    const readOfferLink = (query: Record<string, unknown>) => {
        const one = (key: string): string => {
            const value = query[key]

            return typeof value === 'string' ? value : ''
        }

        const stored = sessionStorage.getItem(OFFER_KEY)

        if (stored) {
            offerToken.value = stored
        } else if (!offerRefused.value) {
            // Not once it has been refused. The guard runs on every navigation and the
            // address the guest is standing on may still carry ?offer=, so without this
            // a token the server has just rejected would be picked straight back up and
            // the same refusal would happen on every screen.
            const token = one('offer')

            if (token) {
                offerToken.value = token
                sessionStorage.setItem(OFFER_KEY, token)
            }
        }

        if (sessionStorage.getItem(SEARCH_KEY)) return

        const from = one('from')
        const to = one('to')

        if (!from || !to) return

        // Not in the past, and at least one night. A link pinned to a post outlives
        // the package it advertised, and putting last October into the form is worse
        // than leaving it empty — the guest picks dates and is told plainly if the
        // offer no longer covers them.
        if (dayjs(from).isBefore(dayjs().startOf('day')) || !dayjs(to).isAfter(dayjs(from))) return

        arrival.value = from
        departure.value = to

        rememberSearch()
    }

    /**
     * Gives up on the offer and goes on at the house's ordinary prices.
     *
     * The token is thrown away rather than kept and retried: the server has just said
     * this promise no longer holds, and every further quote carrying it would be
     * refused the same way.
     */
    const dropOffer = () => {
        offerToken.value = ''
        offerRefused.value = true

        sessionStorage.removeItem(OFFER_KEY)
    }

    /** Remembers the dates and the party, so a reload does not empty the form. */
    const rememberSearch = () => {
        sessionStorage.setItem(SEARCH_KEY, JSON.stringify({
            arrival: arrival.value, departure: departure.value,
            adults: adults.value, children: children.value,
        }))
    }

    const restoreSearch = () => {
        const stored = sessionStorage.getItem(SEARCH_KEY)
        if (!stored) return

        try {
            const value = JSON.parse(stored)

            // Dates that have gone past while the tab sat open are not restored:
            // putting last week back into the form is worse than an empty one.
            if (value.arrival && dayjs(value.arrival).isBefore(dayjs().startOf('day'))) return

            arrival.value = value.arrival ?? ''
            departure.value = value.departure ?? ''
            adults.value = value.adults ?? 2
            children.value = value.children ?? 0
        } catch {
            // Same as above — unreadable storage is simply no storage.
        }
    }

    /**
     * Which house this site sells.
     *
     * One deployment, one house on the web, for this first iteration: the site is
     * served on the hotel's own address, so asking a visitor which hotel they meant
     * would be a question about our deployment rather than about their holiday. A
     * deployment with several houses on sale takes the first — and that is the seam to
     * open when a second one appears, with the house on the hostname or in the path.
     */
    const loadProperty = async (): Promise<void> => {
        loading.value = true

        const { data, error } = await fetchStore.fetch('/booking/properties').json<IBookingProperty[]>()

        loading.value = false

        if (error.value || !data.value || data.value.length === 0) {
            closed.value = true

            return
        }

        // A refusal belongs to the house it was refused about. A different house is a
        // different licence, and this answer came out of the very branch the refusal
        // was on — so the site asks again rather than showing the other hotel's locked
        // door. Driven by an answer that arrived, never by a timer.
        if (data.value[0].id !== property.value?.id) clearModuleRefusal()

        property.value = data.value[0]
        closed.value = false
    }

    /** What is free and what it costs, from the room-nights and the daily rates. */
    const loadOffers = async (): Promise<boolean> => {
        if (!property.value) return false

        loading.value = true
        offers.value = undefined
        componentGone.value = ''

        const query = new URLSearchParams({
            arrival: apiDate(arrival.value),
            departure: apiDate(departure.value),
            adults: String(adults.value),
            children: String(children.value),
        })

        // The offer travels with the quote, because the quote is where its price is
        // worked out. Never a discount and never an amount — those are the server's,
        // read from the offer the token names.
        if (offerToken.value) query.set('offer', offerToken.value)

        // And the campaign this session arrived on, so that the funnel's "booking
        // started" step is counted against the campaign that caused it
        // (docs/MARKETING.md §14.3). It is the SAME attribution that is already stored
        // on the reservation a few lines down — sent here as well rather than derived
        // from the offer, because a quote is not a booking and this is the one step
        // between the two that nothing else records.
        //
        // Nothing about the price depends on it: it is counted and never read back,
        // which is why it is safe for it to come off an address anybody can write.
        if (attribution.value.campaign_id) query.set('cmp', attribution.value.campaign_id)

        if (attribution.value.utm_source) query.set('utm_source', attribution.value.utm_source)

        const { data, error, response } = await fetchStore
            .fetch(`/booking/properties/${property.value.id}/offers?${query.toString()}`)
            .json<IBookingOffers>()

        loading.value = false

        // The house has withdrawn the offer. Not a failure to show the guest: the
        // rooms are still for sale, so the offer is dropped and the same question is
        // asked again at the ordinary price, with the screen saying why the figure
        // changed (docs/MARKETING.md §10).
        if (offerWithdrawn(response.value?.status, data.value as unknown)) {
            dropOffer()

            return loadOffers()
        }

        // The package's own refusal, and the one that is NOT answered by re-quoting:
        // the house still sells it and cannot deliver it on these nights. The token
        // stays, the screen says which part is gone, and the guest picks other dates
        // (docs/MARKETING.md §12).
        const gone = componentSoldOut(response.value?.status, data.value as unknown)

        if (gone) {
            componentGone.value = gone

            return false
        }

        if (error.value || !data.value) return false

        offers.value = data.value

        return true
    }

    /**
     * Makes the booking.
     *
     * What is sent is the stay, the room type, the plan and who the guest is — and no
     * price. The total on the summary screen is what the API quoted, and the API works
     * it out again from its own rates when it books: a number that travelled through a
     * browser is a number the guest could have chosen.
     */
    const book = async (): Promise<{ ok: boolean; status: number }> => {
        if (!property.value || !chosen.value) return { ok: false, status: 0 }

        loading.value = true

        const { data, error, response } = await fetchStore
            .fetch(`/booking/properties/${property.value.id}/reservations`)
            .post({
                arrival:        apiDate(arrival.value),
                departure:      apiDate(departure.value),
                room_type_id:   chosen.value.room_type_id,
                rate_plan_id:   chosen.value.rate_plan_id,
                adults:         adults.value,
                children:       children.value,
                first_name:     firstName.value,
                last_name:      lastName.value,
                email:          email.value,
                phone:          phone.value,
                language:       i18n.global.locale.value,
                notes:          notes.value,
                attribution:    attribution.value,
                offer_token:    offerToken.value,
                terms_accepted: termsAccepted.value,
            })
            .json<IBookingConfirmation>()

        loading.value = false

        // The same withdrawal as on the quote, at the one moment it matters most. The
        // guest is sent back to the rooms rather than booked at a price they were not
        // shown — Summary.vue re-quotes and says what changed.
        if (offerWithdrawn(response.value?.status, data.value as unknown)) {
            dropOffer()

            return { ok: false, status: OFFER_WITHDRAWN_STATUS }
        }

        if (error.value || !data.value) return { ok: false, status: response.value?.status ?? 0 }

        confirmation.value = data.value

        // The visit is over and the next one starts clean. The campaign is kept: a
        // second booking made in the same tab was still brought by the same
        // advertisement.
        sessionStorage.removeItem(SEARCH_KEY)

        return { ok: true, status: 201 }
    }

    /**
     * Opens the card page for the booking that was just made, and answers its address.
     *
     * The amount is not sent. It is the house's configured share of what the stay
     * owes, worked out on the server — this browser could not be trusted with it and
     * is not asked to be.
     */
    const startPayment = async (): Promise<IBookingPaymentPage | null> => {
        const token = confirmation.value?.payment_token
        if (!token) return null

        paying.value = true

        const { data, error } = await fetchStore
            .fetch(`/booking/pay/${token}`)
            .post({})
            .json<IBookingPaymentPage>()

        paying.value = false

        if (error.value || !data.value?.checkout_url) return null

        // Kept so the return from Stripe knows which session to ask about. In session
        // storage rather than in memory because the guest LEAVES this page to pay:
        // Stripe's own page replaces it, and what comes back is a fresh load.
        // The WHOLE confirmation, not just the session. The guest is about to leave
        // this site for Stripe's page, and what comes back is a cold load with an
        // empty store — without this the confirmation page would have no number, no
        // amount and nothing to poll about.
        sessionStorage.setItem(PAYMENT_KEY, JSON.stringify({
            token,
            session: data.value.session_id,
            confirmation: confirmation.value,
            email: email.value,
        }))

        return data.value
    }

    /**
     * What became of the payment, asked of the server.
     *
     * Never of the query string. Stripe sends the guest back with ?paid=1 on success,
     * and that parameter is a hint about which page to draw — never the evidence. The
     * evidence is this call, which reads the transaction the webhook wrote.
     */
    const paymentStatus = async (): Promise<string> => {
        const stored = sessionStorage.getItem(PAYMENT_KEY)
        if (!stored) return ''

        try {
            const { token, session } = JSON.parse(stored)
            if (!token || !session) return ''

            const { data, error } = await fetchStore
                .fetch(`/booking/pay/${token}/status?session=${encodeURIComponent(session)}`)
                .get()
                .json<IBookingPaymentStatus>()

            if (error.value || !data.value) return ''

            return data.value.status
        } catch {
            return ''
        }
    }

    /**
     * Puts back what the guest left behind when they went off to Stripe.
     *
     * Returns whether there was anything to restore, and fills the store as a side
     * effect — the confirmation page then draws exactly as it did before they left,
     * with the number they were given still on it.
     */
    const restorePayment = (): boolean => {
        const stored = sessionStorage.getItem(PAYMENT_KEY)
        if (!stored) return false

        try {
            const parsed = JSON.parse(stored)
            if (!parsed?.confirmation) return false

            confirmation.value = parsed.confirmation
            email.value = parsed.email ?? email.value

            return true
        } catch {
            return false
        }
    }

    const forgetPayment = () => sessionStorage.removeItem(PAYMENT_KEY)

    /** Clears what one booking left behind, for somebody starting another. */
    const startOver = () => {
        offerRefused.value = false
        componentGone.value = ''

        offers.value = undefined
        chosen.value = undefined
        confirmation.value = undefined
        termsAccepted.value = false
        notes.value = ''
    }

    return {
        property, loading, closed, colorMode,
        arrival, departure, adults, children,
        offers, chosen,
        firstName, lastName, email, phone, notes, termsAccepted,
        confirmation, attribution, paying,
        offerToken, offerRefused, offerName, offerDiscountPercent, hasOffer,
        componentGone, components, includedComponents, surchargeComponents, componentsAmount,
        nights, currency, houseLanguage, maxNights,
        readAttribution, readOfferLink, rememberSearch, restoreSearch,
        loadProperty, loadOffers, book, startOver,
        startPayment, paymentStatus, restorePayment, forgetPayment,
    }
})
